import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { AIVoiceFilter, AIVoiceFilterTypeEnum } from 'js/src/client';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';

const getInitialAttributeState = () => PlainJsStoreORM.createEmpty<AIVoiceFilter, number>().data;

const getInitialDataState = () => ({
    accent: getInitialAttributeState(),
    tone: getInitialAttributeState(),
    style: getInitialAttributeState(),
    language: getInitialAttributeState(),
    age: getInitialAttributeState(),
    gender: getInitialAttributeState(),
    quality: getInitialAttributeState(),
});

type AIVoiceFiltersQueryStatus = 'uninitialized' | 'pending' | 'fulfilled' | 'rejected';

type AIVoiceFiltersRequestStatus = {
    status: AIVoiceFiltersQueryStatus;
    requestId: string | null;
    error: string | null;
};

const getInitialRequestState = (): AIVoiceFiltersRequestStatus => ({
    status: 'uninitialized',
    requestId: null,
    error: null,
});

const getInitialState = () => ({
    data: getInitialDataState(),
    request: getInitialRequestState(),
});

export const aiVoiceFiltersSlice = createSlice({
    name: 'aiVoiceFilters',
    initialState: getInitialState(),
    reducers: {
        setAttributes: (
            state,
            action: PayloadAction<{
                filterType: AIVoiceFilterTypeEnum;
                data: AIVoiceFilter[];
            }>,
        ) => {
            const { filterType, data } = action.payload;
            const orm = new PlainJsStoreORM(state.data[filterType]);
            data.forEach((item) => orm.pushItem(item, 'id'));
            state.data[filterType] = orm.data;
        },
        setAIVoiceFiltersRequestStarted: (state, action: PayloadAction<string>) => {
            state.request.status = 'pending';
            state.request.requestId = action.payload;
            state.request.error = null;
        },
        setAIVoiceFiltersRequestSucceeded: (
            state,
            action: PayloadAction<{ requestId: string }>,
        ) => {
            if (state.request.requestId !== action.payload.requestId) {
                return;
            }
            state.request.status = 'fulfilled';
            state.request.error = null;
        },
        setAIVoiceFiltersRequestFailed: (
            state,
            action: PayloadAction<{ requestId: string; error: string }>,
        ) => {
            if (state.request.requestId !== action.payload.requestId) {
                return;
            }
            state.request.status = 'rejected';
            state.request.error = action.payload.error;
        },
    },
});

export const {
    setAttributes,
    setAIVoiceFiltersRequestStarted,
    setAIVoiceFiltersRequestSucceeded,
    setAIVoiceFiltersRequestFailed,
} = aiVoiceFiltersSlice.actions;
